/**
 * Indigo Dream Theme
 * Modern and elegant theme with purple and cyan accents
 * Primary: Deep Indigo (#4F46E5), Secondary: Purple (#A855F7), Accent: Cyan (#06B6D4)
 */

import { indigoDreamColors } from '../tokens/colors.js';
import { spacing, zIndex } from '../tokens/spacing.js';
import { typography } from '../tokens/typography.js';
import { borderRadius, shadows, transitions } from '../tokens/effects.js';

export const indigoDreamTheme = {
  name: 'indigo-dream',
  label: 'Indigo Dream',
  description: 'Modern theme with deep indigo and vibrant purple accents',
  isDark: false,

  // Color palette
  colors: {
    primary: indigoDreamColors.primary,
    primaryLight: indigoDreamColors.primaryLight,
    primaryDark: indigoDreamColors.primaryDark,
    primaryHover: indigoDreamColors.primaryDark,
    primaryOpacity: indigoDreamColors.primaryOpacity20,

    secondary: indigoDreamColors.secondary,
    secondaryLight: indigoDreamColors.secondaryLight,
    secondaryDark: indigoDreamColors.secondaryDark,
    secondaryOpacity: indigoDreamColors.secondaryOpacity20,

    tertiary: indigoDreamColors.tertiary,
    tertiaryLight: indigoDreamColors.tertiaryLight,
    tertiaryDark: indigoDreamColors.tertiaryDark,
    tertiaryOpacity: indigoDreamColors.tertiaryOpacity20,

    accent: indigoDreamColors.accent,
    accentLight: indigoDreamColors.accentLight,
    accentDark: indigoDreamColors.accentDark,
    accentOpacity: indigoDreamColors.accentOpacity20,

    // Semantic colors
    error: indigoDreamColors.error,
    errorLight: indigoDreamColors.errorLight,
    success: indigoDreamColors.success,
    successLight: indigoDreamColors.successLight,
    warning: indigoDreamColors.warning,
    warningLight: indigoDreamColors.warningLight,
    info: indigoDreamColors.info,
    infoLight: indigoDreamColors.infoLight,

    // Neutrals
    background: indigoDreamColors.backgroundLight,
    backgroundAlt: indigoDreamColors.backgroundDark,
    backgroundDark: indigoDreamColors.backgroundDark,
    backgroundNeutral: '#F0F0F0',
    surface: indigoDreamColors.surfaceLight,
    surfaceElevated: indigoDreamColors.surfaceElevated,

    // Text
    textPrimary: indigoDreamColors.darkGray900,
    textSecondary: indigoDreamColors.mediumGray600,
    textTertiary: indigoDreamColors.mediumGray500,
    textInverse: indigoDreamColors.white,

    // Borders
    borderDefault: indigoDreamColors.lightGray300,
    borderSubtle: indigoDreamColors.lightGray200,
    borderInput: indigoDreamColors.borderInput,
  },

  // Spacing
  spacing,

  // Z-index
  zIndex,

  // Typography
  typography,

  // Border radius
  borderRadius,

  // Shadows
  shadows,

  // Transitions
  transitions,

  // Component-specific tokens
  components: {
    button: {
      paddingX: spacing.base,
      paddingY: '10px',
      borderRadius: borderRadius.md,
      transition: transitions.default,
      fontSize: typography.fontSize.body,
      fontWeight: typography.fontWeight.semibold,
      // SYN-2738 (A9): fundos/texto do DSButton por tema (--button-*).
      primaryBg: indigoDreamColors.primary,
      primaryBgHover: indigoDreamColors.primaryDark,
      // C12 (2026-10-05): texto do botão ghost/de texto. Aqui = a cor primária de sempre.
      ghostText: indigoDreamColors.primary,
      secondaryBg: indigoDreamColors.secondary,
      secondaryBgHover: indigoDreamColors.secondaryDark,
      accentText: indigoDreamColors.darkGray900,
    },
    card: {
      padding: spacing.xl,
      borderRadius: borderRadius.lg,
      backgroundColor: indigoDreamColors.surfaceLight,
      boxShadow: shadows.base,
    },
    input: {
      paddingX: spacing.base,
      paddingY: '10px',
      borderRadius: borderRadius.md,
      borderColor: indigoDreamColors.lightGray300,
      focusBorderColor: indigoDreamColors.primary,
      fontSize: typography.fontSize.body,
    },
    chip: {
      paddingX: spacing.md,
      paddingY: spacing.xs,
      borderRadius: borderRadius.full,
      fontSize: typography.fontSize.labelSmall,
      fontWeight: typography.fontWeight.medium,
    },
    badge: {
      paddingX: spacing.sm,
      paddingY: spacing.xs,
      borderRadius: borderRadius.sm,
      fontSize: typography.fontSize.labelSmall,
      fontWeight: typography.fontWeight.bold,
    },
    sidebar: {
      backgroundColor: indigoDreamColors.backgroundLight,
      iconSelected: indigoDreamColors.primary,
      iconDefault: indigoDreamColors.mediumGray600,
      iconDisabled: indigoDreamColors.lightGray400,
      iconStatic: indigoDreamColors.darkGray800,
      divider: indigoDreamColors.lightGray300,
    },
  },

  // Vuetify theme configuration
  vuetifyTheme: {
    dark: false,
    colors: {
      primary: indigoDreamColors.primary,
      secondary: indigoDreamColors.secondary,
      accent: indigoDreamColors.accent,
      error: indigoDreamColors.error,
      warning: indigoDreamColors.warning,
      info: indigoDreamColors.info,
      success: indigoDreamColors.success,
      background: indigoDreamColors.backgroundLight,
      surface: indigoDreamColors.surfaceLight,
    },
  },
};
